import { Language } from "./Language.js";
import { IData, VOConfig, VOGroup, VOMedia, VOVideo } from "./Structure.js";

class DataManager {

    public onError = e5.Signal.create<[Error]>();
    public data: IData = null;
    public config: VOConfig = null;

    public relatedVideos: VOVideo[] = [];

    private _defaultVideoId: number;

    public async load(dataUrl: string, defaultVideoId: number): Promise<VOVideo | null> {
        this._defaultVideoId = defaultVideoId;

        return new Promise((resolve, reject) => {
            var ajax = new XMLHttpRequest();
            ajax.onreadystatechange = (e: Event) => {
                if (ajax.readyState == 4) {
                    if (ajax.status == 200) {
                        resolve(this.finalize(ajax.response));
                    }
                    else {
                        const msg = "Leider konnten die Video-Daten nicht geladen werden";
                        this.onError.dispatch(new Error(msg));
                        resolve(null);
                    }
                }
                else if (ajax.readyState == 0) {
                    console.log("REQUEST NOT INIT");
                }
            }
            ajax.responseType = "text";
            ajax.open("get", dataUrl);
            ajax.send();
        });
    }

    private finalize(response: any): VOVideo {
        this.data = JSON.parse(response);

        Language.updateLabels(this.data.ui_labels);

        for (var group of this.data.group) {
            if (group.videos) {
                var videos: VOVideo[] = [];
                for (var i = 0; i < group.videos.length; ++i) {
                    var id: number = <any>group.videos[i];
                    var v = e5.array.id(this.data.video, id);
                    if (v)
                        videos.push(v);
                    else
                        console.warn(`Could not find video "${id}" for group "${group.title}".`);
                }
                group.videos = videos;
            } else {
                group.videos = [];
            }
        }

        for (var config of this.data.config) {
            config.default_video = e5.array.id(this.data.video, <any>config.default_video);

            config.groups = config.groups || [];
            config.welcome = e5.array.id(this.data.welcome, <any>config.welcome);

            if (config.groups) {
                for (var i = 0; i < config.groups.length; ++i) {
                    config.groups[i] = e5.array.id(this.data.group, <any>config.groups[i]);
                }
            } else {
                config.groups = [];
            }

            config.default_video = config.default_video ? config.default_video : (config.groups ? config.groups[0].videos[0] : this.data.video[0]);
        }

        this.config = e5.array.match(this.data.config, "title", e5.Url.params.get('config', "Default")) || this.data.config[0];

        for (var video of this.data.video) {
            this.prepareVideo(video);
        }

        var hash: string = window.location.hash;
        hash = hash.substring(1);

        var video: VOVideo = null;
        if (hash) {
            var spl = hash.split("/");
            video = this.getVideo(parseInt(spl[1]));
        }

        if (!video) {
            video = this.getVideo(this._defaultVideoId);
        }

        if (!video) {
            video = this.config.default_video;
        }

        if (this.config.show_ungrouped_videos) {

            var videos = this.getRelatedVideos(video);
            var groupVideos: VOVideo[] = [];
            for (var group of this.config.groups) {
                if (group.videos) {
                    for (var v of group.videos) {
                        if (groupVideos.indexOf(v) === -1)
                            groupVideos.push(v);
                        this.getRelatedVideos(v, videos);
                    }
                }
            }

            var ungroupedVideos: VOVideo[] = [];
            for (var v of videos) {
                if (groupVideos.indexOf(v) === -1) {
                    console.warn("Ungrouped video found:", v.title, v.id);
                    ungroupedVideos.push(v);
                }
            }

            if (ungroupedVideos.length > 0) {
                var group: VOGroup = {
                    id: -1,
                    color: "#444444",
                    title: "Default Group",
                    videos: ungroupedVideos
                };
                this.data.group.unshift(group);
                this.config.groups.unshift(group);
            }
        }

        return video;
    }

    private getRelatedVideos(video: VOVideo, collection: VOVideo[] = []): VOVideo[] {
        if (!video || collection.indexOf(video) !== -1)
            return collection;

        collection.push(video);

        //Look for videos which are referenced by the current video.
        if (video.next_video) {
            this.getRelatedVideos(this.getVideo(video.next_video), collection);
        }
        if (video.markers) {
            for (var m of video.markers) {
                if (m.marker_action === "video") {
                    this.getRelatedVideos(this.getVideo(m.marker_video), collection);
                }
            }
        }

        //Look for videos which reference the current video.
        for (var v of this.data.video) {
            if (v.next_video === video.id) {
                this.getRelatedVideos(v, collection);
            }
            if (v.markers) {
                for (var m of v.markers) {
                    if (m.marker_action === "video" && m.marker_video === video.id) {
                        this.getRelatedVideos(v, collection);
                    }
                }
            }
        }

        return collection;
    }

    private prepareMedia(media: VOMedia): void {
        if (!media)
            return;

        // console.log(media);
        if (media.sizes && !media.sizes.full) {
            if (media.sizes.thumbnail) {
                media.sizes.full = media.sizes.thumbnail.replace("-150x150", "");
            }
        }
    }

    public prepareVideo(video: VOVideo): void {
        video.camera_x = parseFloat(<any>video.camera_x);
        video.camera_y = parseFloat(<any>video.camera_y);
        video.icon = video.icon;

        if (video.next_video
            && video.next_video == video.id) {
            console.warn("Next video given is self referencing.", video);
            video.next_video = null;
        }

        video.markers = video.markers || [];

        this.prepareMedia(video.video_desktop);
        this.prepareMedia(video.video_mobile);

        if (!video.title_de && !video.title_en) {
            video.title_de = video.title;
            video.title_en = video.title;
        }

        for (var marker of video.markers) {
            marker.isInTime = true;
            marker.delegates = [];
            if (marker.marker_start_time == undefined)
                marker.marker_start_time = -1;
            if (marker.marker_end_time == undefined)
                marker.marker_end_time = -1;

            if (!marker.marker_icon) {
                var icon: string = "";
                if (marker.marker_action == "video") {
                    if (this.data) {
                        var v = e5.array.id(this.data.video, marker.marker_video);
                        if (v && v.icon) {
                            icon = v.icon;
                        }
                    }
                    if (!icon)
                        icon = "icon-479";
                }
                else if (marker.marker_action == "text") {
                    icon = "icon-463";
                }
                marker.marker_icon = icon;
            }
            else {
                marker.marker_icon = marker.marker_icon;
            }
            marker.marker_x = parseFloat(<any>marker.marker_x);
            marker.marker_y = parseFloat(<any>marker.marker_y);
        }
    }

    public getVideo(id: number): VOVideo {
        return this.data ? e5.array.id(this.data.video, id) : null;
    }
}

export const store = new DataManager();